import { UploadPhotoTemplate } from '@templates';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ErrorMessages, ServiceLocationDetailsObjectType } from '@types';
import * as Sentry from '@sentry/react';
import { FileObject, useDeleteImage, useUploadImage } from '@hooks';
import {
  convertFilePartsToFileObjects,
  handleFileChange,
  handleFileDelete,
  handleUpdateUploadedImages,
  handleUploadFile,
  PartitionFileObjectsType,
} from '@utils';
import { useAnimalNuisanceStore } from '@animal-nuisance/hooks/useAnimalNuisanceStore';
import { updateStoreByKey } from '@animal-nuisance/hooks/useAnimalNuisanceStore';
import { RoutesList } from '@animal-nuisance/routes/Routes';
import { UploadPhotoTextContent } from '@organisms/UploadPhotoForm/UploadPhotoForm';

export const UploadPhotoPage = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState<FileObject[]>([]);
  const {
    update,
    data: {
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
      fileWithLoadedCoordinates,
      serviceLocationDetails,
    },
  } = useAnimalNuisanceStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  //use mutateAsync due to async operations after successful upload
  const { mutateAsync: mutateImageUpload, isPending } = useUploadImage();
  const { mutate: mutateDelete } = useDeleteImage();
  const [processingPhoto, setProcessingPhoto] = useState(false);

  useEffect(() => {
    convertFilePartsToFileObjects({
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
    }).then((images) => {
      setUploadedFiles(images);
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFileChange = async (files: File[]) => {
    setProcessingPhoto(true);
    await handleFileChange({
      files,
      setProcessingPhoto,
      uploadedFiles,
      setUploadedFiles,
      setErrorMessages,
      updateUploadedImages: (images: PartitionFileObjectsType) =>
        handleUpdateUploadedImages(
          images,
          serviceLocationDetails,
          updateStoreByKey,
        ),
      updateIsPhotoLocationStored: (isPhotoLocationStored: boolean) =>
        update('isPhotoLocationStored', isPhotoLocationStored),
      updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) =>
        update('fileWithLoadedCoordinates', fileWithLoadedCoordinates),
      updateServiceLocationDetails: (
        serviceLocationDetails: ServiceLocationDetailsObjectType,
      ) => update('serviceLocationDetails', serviceLocationDetails),
      errorPath: 'upload-photo',
    });
    setProcessingPhoto(false);
  };

  const handleDelete = (files: File[]) => {
    handleFileDelete({
      files,
      setProcessingPhoto,
      uploadedFiles,
      setUploadedFiles,
      setErrorMessages,
      updateUploadedImages: (images: PartitionFileObjectsType) =>
        handleUpdateUploadedImages(
          images,
          serviceLocationDetails,
          updateStoreByKey,
        ),
      updateIsPhotoLocationStored: (isPhotoLocationStored: boolean) =>
        update('isPhotoLocationStored', isPhotoLocationStored),
      fileWithLoadedCoordinates,
      updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) =>
        update('fileWithLoadedCoordinates', fileWithLoadedCoordinates),
      updateServiceLocationDetails: (
        serviceLocationDetails: ServiceLocationDetailsObjectType,
      ) => update('serviceLocationDetails', serviceLocationDetails),
      mutateDelete,
      errorPath: 'upload-photo',
    });
  };

  const handleUploadError = (error: string) => {
    if (error) {
      Sentry.captureException(error);
      setErrorMessages([{ path: 'upload-photo', message: error }]);
    }
  };

  const buttonNext = {
    label: 'I do not have a photo',
    loading: isPending,
    disabled: processingPhoto,
    onClick: async () => {
      await handleUploadFile({
        uploadedFiles,
        uploadedImagesBlob,
        navigate,
        navigatePath: RoutesList.ISSUE_LOCATION,
        mutateImageUpload,
        errorPath: 'upload-photo',
        setErrorMessages,
        updateUploadedImagesBlobs: (
          imagesBlobs: {
            blob_uri: string;
            blob_endpoint: string;
            filename: string;
          }[],
        ) => update('uploadedImagesBlob', imagesBlobs),
      });
    },
  };

  const buttonPrevious = {
    disabled: processingPhoto || isPending,
    onClick: () => {
      navigate(RoutesList.IS_RECURRING_ISSUE);
    },
  };
  return (
    <UploadPhotoTemplate
      formTitle="Upload photos of the issue"
      description={<UploadPhotoTextContent />}
      handleFileChange={onFileChange}
      handleUploadError={handleUploadError}
      handleDelete={handleDelete}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      maxNumberOfUpload={5}
      errorPath={'upload-photo'}
      errorMessages={errorMessages}
      initialFiles={uploadedFiles.map((file) => file.file)}
    />
  );
};
